//  FIXME: A lot of this logic was taken from the old DPC, and needs to be cleaned up

import { ChangeEvent, MouseEventHandler, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  SaveOutlined as SaveIcon,
  GpsFixed as PointIcon,
  ShowChart as LineIcon,
  Architecture as ArcIcon,
  // ControlCamera as KeyboardInputIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { CoordinateApiModel } from '../../../../types/models/coordinate.model';
import { useCreateSymbol, useGetSymbol, useSearchLibraries, useUpdateSymbol } from '../../hooks/api/use-symbols.hook';
import { SymbolApiModel, SymbolRelationship, WithCoordinates } from '../../../../types/models/symbol.model';
import { SymbolForm } from './SymbolForm';
import { symbolColors } from '../../../../types/constants/symbol-colors';
import { SymbolViewer } from './SymbolViewer';
import { LoadingIndicator } from '../LoadingIndicator';

type Coordinate = CoordinateApiModel & { id?: string; name?: string; highlight?: boolean };

export const AddEditSymbol = () => {
  const navigate = useNavigate();
  const { symbolId } = useParams<{ libraryId: string; symbolId: string }>();

  const { symbol, loading } = useGetSymbol<SymbolApiModel & WithCoordinates>({
    symbolId: Number(symbolId),
    include: [SymbolRelationship.COORDINATES],
  });
  const [createSymbol, { error: createSymbolError }] = useCreateSymbol();
  const [updateSymbol, { error: updateSymbolError }] = useUpdateSymbol();
  const { loading: loadingLibraries, response: librariesResponse, error: librariesError } = useSearchLibraries({});

  const [coordinates, setCoordinates] = useState<Coordinate[]>(symbol?.coordinates ?? []);
  const [disableColor, setDisableColor] = useState(symbol?.coordinates?.[0].cx === 0);
  const [selectedCoordinate, setSelectedCoordinate] = useState<Coordinate | null>(null);
  const [selectedHelper, setSelectedHelper] = useState<Coordinate | null>(null);
  const [componentColorSelectAnchor, setComponentColorSelectAnchor] = useState<HTMLElement | null>(null);
  const [selectedColor, setSelectedColor] = useState(0);
  // const [selectedKeyBoardInput, setSelectedKeyboardInput] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [snapGridSize, setSnapGridSize] = useState(10);

  useEffect(() => {
    if (symbol) {
      setCoordinates(symbol.coordinates.map((c, index) => ({ ...c, id: `${index}` })));
      setDisableColor(symbol.coordinates[0].cx === 0);
    }
  }, [symbol]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<SymbolForm>({ resolver: classValidatorResolver(SymbolForm), values: symbol });

  const onSubmit: SubmitHandler<SymbolForm> = async ({ library, ...values }) => {
    await (symbol
      ? updateSymbol({ ...values, symbolId: symbol.id, coordinates })
      : createSymbol({ ...values, libraryId: library.id, coordinates }));
    navigate('/symbols');
  };

  const renderColorChoices = (onClick: (colorIndex: number) => void) => (
    <Box>
      <Stack direction="row" spacing={1} style={{ margin: '6px 4px' }}>
        {symbolColors.slice(0, symbolColors.length / 2).map((color, index) => (
          <Box
            key={index}
            style={{
              cursor: 'pointer',
              width: '18px',
              height: '18px',
              border: '1px solid #333',
              backgroundColor: color,
            }}
            onClick={() => onClick(index)}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1} style={{ margin: '6px 4px' }}>
        {symbolColors.slice(symbolColors.length / 2, symbolColors.length).map((color, index) => (
          <Box
            key={symbolColors.length / 2 + index}
            style={{
              cursor: 'pointer',
              width: '18px',
              height: '18px',
              border: '1px solid #333',
              backgroundColor: color,
            }}
            onClick={() => onClick(symbolColors.length / 2 + index)}
          />
        ))}
      </Stack>
    </Box>
  );

  const handleDisableColor = (checked: boolean) => {
    setDisableColor(checked);
    const existingComponents = [...coordinates];
    if (checked) {
      setSelectedColor(0);
      existingComponents[0].cx = 0;
    } else {
      existingComponents[0].cx = 1;
    }
    setCoordinates(existingComponents);
  };

  const onComponentClick = (coord: Coordinate) => {
    setSelectedCoordinate(coord);
    const newCoordinates: Coordinate[] = coordinates
      .filter((c) => c.id !== 'helper1' && c.id !== 'helper2' && c.id !== 'helper3' && c.id !== 'helper4');

    const baseProperties = { x2: 0, y2: 0, cx: 0, cy: 0, largeArcSweep: false };
    if (coord.type === 'Line') {
      newCoordinates.push({ ...baseProperties, type: 'Point', x1: coord.x1, y1: coord.y1, color: 0, id: 'helper1' });
      newCoordinates.push({ ...baseProperties, type: 'Point', x1: coord.x2, y1: coord.y2, color: 0, id: 'helper2' });
    }
    if (coord.type === 'Arc') {
      newCoordinates.push({ ...baseProperties, type: 'Point', x1: coord.x1, y1: coord.y1, color: 0, id: 'helper1' });
      newCoordinates.push({ ...baseProperties, type: 'Point', x1: coord.x2, y1: coord.y2, color: 0, id: 'helper2' });
      newCoordinates.push({ ...baseProperties, type: 'Point', x1: coord.cx, y1: coord.cy, color: 0, id: 'helper3' });
    }

    setCoordinates(newCoordinates);
  };

  const onClear = () => {
    const newCoordinates: Coordinate[] = coordinates.filter(
      (c) => c.id !== 'helper1' && c.id !== 'helper2' && c.id !== 'helper3' && c.id !== 'helper4',
    );
    setCoordinates(newCoordinates);
    setSelectedCoordinate(null);
    // setSelectedKeyboardInput(null);
  };

  const handleComponentColorChange = (colorId: number) => {
    const coordinate = coordinates.find((c) => c.id === selectedCoordinate?.id);
    if (coordinate) {
      coordinate.color = colorId;
    }
    setCoordinates([...coordinates]);
  };

  const handleComponentDetailsChange = (
    param: keyof Pick<CoordinateApiModel, 'x1' | 'y1' | 'x2' | 'y2' | 'cx' | 'cy' | 'largeArcSweep'>,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const coordinate = coordinates.find((c) => c.id === selectedCoordinate?.id);
    const helper1 = coordinates.find((c) => c.id === 'helper1');
    const helper2 = coordinates.find((c) => c.id === 'helper2');
    const helper3 = coordinates.find((c) => c.id === 'helper3');
    if (!coordinate) {
      return;
    }

    if (coordinate.id === selectedCoordinate?.id && param !== 'largeArcSweep') {
      coordinate[param] = Number(e.target.value);
    } else if (coordinate.id === selectedCoordinate?.id && param === 'largeArcSweep') {
      coordinate[param] = (e.target as HTMLInputElement).checked;
    }
    if ((param === 'x1' || param === 'y1') && helper1) {
      helper1[param] = Number(e.target.value);
    }
    if (param === 'x2' && helper2) {
      helper2.x1 = Number(e.target.value);
    }
    if (param === 'y2' && helper2) {
      helper2.y1 = Number(e.target.value);
    }
    if (param === 'cx' && helper3) {
      helper3.x1 = Number(e.target.value);
    }
    if (param === 'cy' && helper3) {
      helper3.y1 = Number(e.target.value);
    }
    setCoordinates([...coordinates]);
  };

  const handleComponentDelete = (componentId?: string) => {
    setCoordinates(
      coordinates.filter(
        (c) =>
          c.id !== componentId && c.id !== 'helper1' && c.id !== 'helper2' && c.id !== 'helper3' && c.id !== 'helper4',
      ),
    );
    setSelectedCoordinate(null);
  };

  /* useEffect(() => {
    window.addEventListener('keydown', handleKeyBoardEvent);

    return () => {
      window.removeEventListener('keydown', handleKeyBoardEvent);
    };
  }, [selectedKeyBoardInput]);

  useEffect(() => {
    setSelectedKeyboardInput(null);
  }, [selectedCoordinate]); */

  const handleAdd = (type: CoordinateApiModel['type']) => {
    onClear();

    const newCoordinate: Coordinate = {
      id: `${coordinates.length}`,
      color: selectedColor,
      type,
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 0,
      cx: 0,
      cy: 0,
      largeArcSweep: false,
      highlight: true,
    };

    if (type === 'Point') {
      newCoordinate.x1 = 100;
      newCoordinate.y1 = 100;
    } else if (type === 'Line') {
      newCoordinate.x1 = 75;
      newCoordinate.x2 = 125;
      newCoordinate.y1 = 100;
      newCoordinate.y2 = 100;
    } else if (type === 'Arc') {
      newCoordinate.x1 = 75;
      newCoordinate.x2 = 125;
      newCoordinate.y1 = 100;
      newCoordinate.y2 = 100;
      newCoordinate.cx = 100;
      newCoordinate.cy = 125;
    }

    const newCoordinates = coordinates.filter(
      (c) => c.id !== 'helper1' && c.id !== 'helper2' && c.id !== 'helper3' && c.id !== 'helper4',
    );
    newCoordinates.push(newCoordinate);

    const baseProperties = { x2: 0, y2: 0, cx: 0, cy: 0, largeArcSweep: false };
    if (type === 'Line') {
      newCoordinates.push({
        ...baseProperties,
        type: 'Point',
        x1: newCoordinate.x1,
        y1: newCoordinate.y1,
        color: 0,
        id: 'helper1',
      });
      newCoordinates.push({
        ...baseProperties,
        type: 'Point',
        x1: newCoordinate.x2,
        y1: newCoordinate.y2,
        color: 0,
        id: 'helper2',
      });
    }
    if (type === 'Arc') {
      newCoordinates.push({
        ...baseProperties,
        type: 'Point',
        x1: newCoordinate.x1,
        y1: newCoordinate.y1,
        color: 0,
        id: 'helper1',
      });
      newCoordinates.push({
        ...baseProperties,
        type: 'Point',
        x1: newCoordinate.x2,
        y1: newCoordinate.y2,
        color: 0,
        id: 'helper2',
      });
      newCoordinates.push({
        ...baseProperties,
        type: 'Point',
        x1: newCoordinate.cx,
        y1: newCoordinate.cy,
        color: 0,
        id: 'helper3',
      });
    }

    setCoordinates(newCoordinates);
    setSelectedCoordinate(newCoordinate);
  };

  const handleMouseMove: MouseEventHandler = (e) => {
    const div = document.getElementById('svg');
    if (!div) {
      return;
    }

    const bounds = div.getBoundingClientRect();
    let x = (e.clientX - bounds.left) / 3;
    let y = 200 - (e.clientY - bounds.top) / 3;
    x = Math.round(x);
    y = Math.round(y);

    if (snapToGrid) {
      x = Math.round(x / snapGridSize) * snapGridSize;
      y = Math.round(y / snapGridSize) * snapGridSize;
    }

    setMousePos({ x, y });

    if (selectedHelper != null) {
      const coordinate = coordinates.find((c) => c.id === selectedCoordinate?.id);
      const helper = coordinates.find((c) => c.id === selectedHelper?.id);
      if (!helper || !coordinate) {
        return;
      }

      if (helper.id === selectedHelper.id) {
        helper.x1 = x;
        helper.y1 = y;
      }
      if (selectedHelper.id === 'helper1') {
        coordinate.x1 = x;
        coordinate.y1 = y;
      } else if (selectedHelper.id === 'helper2') {
        coordinate.x2 = x;
        coordinate.y2 = y;
      } else if (selectedHelper.id === 'helper3') {
        coordinate.cx = x;
        coordinate.cy = y;
      }
      setCoordinates([...coordinates]);
    }
  };

  const handleHelperMove = (coordinate: Coordinate) => {
    if (coordinate === null) {
      setSelectedHelper(null);
      return;
    }
    setSelectedHelper(coordinate);
  };

  const renderSelectedCoordinate = () => {
    if (!selectedCoordinate) {
      return (
        <Typography variant="body2" style={{ padding: '6px' }}>
          No point, line or arc selected...
        </Typography>
      );
    }

    return (
      <Box display="flex" flexDirection="column" style={{ padding: '6px' }}>
        <Typography variant="button">Type: {selectedCoordinate.type}</Typography>
        {!disableColor ? (
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box>
              <Typography variant="button">Color:</Typography>
            </Box>
            <Box style={{ marginLeft: '10px' }}>
              <Box
                style={{
                  cursor: 'pointer',
                  width: '18px',
                  height: '18px',
                  border: '1px solid #333',
                  backgroundColor: symbolColors[selectedCoordinate.color],
                }}
                onClick={(e) => setComponentColorSelectAnchor(e.currentTarget)}
              />
              <Popover
                open={componentColorSelectAnchor !== null}
                anchorEl={componentColorSelectAnchor}
                onClose={() => setComponentColorSelectAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {renderColorChoices((colorIndex) => handleComponentColorChange(colorIndex))}
              </Popover>
            </Box>
          </Box>
        ) : null}
        {selectedCoordinate.type === 'Point' ? (
          <>
            <Box display="flex" flexDirection="row" alignItems="center" style={{ marginTop: '1rem' }}>
              {/* <Box>
                <Button
                  disabled={isSubmitting}
                  style={{ color: '#2FFF00' }}
                  size="small"
                  variant={selectedKeyBoardInput == 'Point' ? 'contained' : 'outlined'}
                >
                  <KeyboardInputIcon />
                </Button>
              </Box> */}
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="pointX1"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.x1}
                  label="X"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('x1', e)}
                />
              </Box>
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="pointY1"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.y1}
                  label="Y"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('y1', e)}
                />
              </Box>
            </Box>
          </>
        ) : null}
        {selectedCoordinate.type === 'Line' ? (
          <>
            <Box display="flex" flexDirection="row" alignItems="center" style={{ marginTop: '1rem' }}>
              {/* <Box>
                <Button
                  disabled={isSubmitting}
                  style={{ color: '#2FFF00' }}
                  size="small"
                  variant={selectedKeyBoardInput == 'LineStart' ? 'contained' : 'outlined'}
                >
                  <KeyboardInputIcon />
                </Button>
              </Box> */}
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="lineStartX"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.x1}
                  label="Start X"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('x1', e)}
                />
              </Box>
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="lineStartY"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.y1}
                  label="Start Y"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('y1', e)}
                />
              </Box>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center" style={{ marginTop: '1rem' }}>
              {/* <Box>
                <Button
                  disabled={isSubmitting}
                  style={{ color: '#FF0000' }}
                  size="small"
                  variant={selectedKeyBoardInput == 'LineEnd' ? 'contained' : 'outlined'}
                >
                  <KeyboardInputIcon />
                </Button>
              </Box> */}
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="lineEndX"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.x2}
                  label="End X"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('x2', e)}
                />
              </Box>
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="lineEndY"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.y2}
                  label="End Y"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('y2', e)}
                />
              </Box>
            </Box>
          </>
        ) : null}
        {selectedCoordinate.type === 'Arc' ? (
          <>
            <Box display="flex" flexDirection="row" alignItems="center" style={{ marginTop: '1rem' }}>
              {/* <Box>
                <Button
                  disabled={isSubmitting}
                  style={{ color: '#2FFF00' }}
                  size="small"
                  variant={selectedKeyBoardInput == 'ArcStart' ? 'contained' : 'outlined'}
                >
                  <KeyboardInputIcon />
                </Button>
              </Box> */}
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="arcStartX"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.x1}
                  label="Start X"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('x1', e)}
                />
              </Box>
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="arcStartY"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.y1}
                  label="Start Y"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('y1', e)}
                />
              </Box>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center" style={{ marginTop: '1rem' }}>
              {/* <Box>
                <Button
                  disabled={isSubmitting}
                  style={{ color: '#FF0000' }}
                  size="small"
                  variant={selectedKeyBoardInput == 'ArcEnd' ? 'contained' : 'outlined'}
                >
                  <KeyboardInputIcon />
                </Button>
              </Box> */}
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="arcEndX"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.x2}
                  label="End X"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('x2', e)}
                />
              </Box>
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="arcEndY"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.y2}
                  label="End Y"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('y2', e)}
                />
              </Box>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center" style={{ marginTop: '1rem' }}>
              {/* <Box>
                <Button
                  disabled={isSubmitting}
                  style={{ color: '#9900FF' }}
                  size="small"
                  variant={selectedKeyBoardInput == 'ArcCenter' ? 'contained' : 'outlined'}
                >
                  <KeyboardInputIcon />
                </Button>
              </Box> */}
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="arcCenterX"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.cx}
                  label="Center X"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('cx', e)}
                />
              </Box>
              <Box>
                <TextField
                  disabled={isSubmitting}
                  id="arcCenterY"
                  type="number"
                  variant="outlined"
                  value={selectedCoordinate.cy}
                  label="Center Y"
                  size="small"
                  style={{ margin: '0 4px' }}
                  onChange={(e) => handleComponentDetailsChange('cy', e)}
                />
              </Box>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center" style={{ marginTop: '1rem' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={isSubmitting}
                    checked={selectedCoordinate.largeArcSweep}
                    style={{ margin: '0 4px' }}
                    onChange={(e) => handleComponentDetailsChange('largeArcSweep', e)}
                  />
                }
                label="Counter Clockwise Arc"
              />
            </Box>
          </>
        ) : null}
        <Box display="flex" flexDirection="row" alignItems="center" style={{ marginTop: '1rem' }}>
          <Box>
            <Button color="error" variant="outlined" onClick={() => handleComponentDelete(selectedCoordinate.id)}>
              Delete
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderActions = () => (
    <Box display="flex" flexDirection="column" justifyContent="center">
      <Box>
        <Button
          disabled={isSubmitting}
          variant="outlined"
          startIcon={<PointIcon />}
          style={{ margin: '6px' }}
          onClick={() => handleAdd('Point')}
        >
          Add Point
        </Button>
      </Box>
      <Box>
        <Button
          disabled={isSubmitting}
          variant="outlined"
          startIcon={<LineIcon />}
          style={{ margin: '6px' }}
          onClick={() => handleAdd('Line')}
        >
          Add Line
        </Button>
      </Box>
      <Box>
        <Button
          disabled={isSubmitting}
          variant="outlined"
          startIcon={<ArcIcon />}
          style={{ margin: '6px' }}
          onClick={() => handleAdd('Arc')}
        >
          Add Arc
        </Button>
      </Box>
    </Box>
  );

  if (librariesError) {
    return <Alert severity="error">{librariesError.response?.data.message}</Alert>;
  } else if (loading || loadingLibraries) {
    return <LoadingIndicator />;
  }

  return (
    <Box sx={{ height: '100%' }}>
      <Box display="flex" flexDirection="column" sx={{ p: '1.5rem' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Box display="flex" flexDirection="row" alignItems="center">
              <Box>
                <TextField
                  {...register('name')}
                  variant="outlined"
                  label="Name"
                  disabled={isSubmitting}
                  size="small"
                  style={{ width: '300px' }}
                  autoFocus={!symbolId}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                />
                <Controller
                  control={control}
                  name="library"
                  render={({ field }) => (
                    <Autocomplete
                      {...register('library')}
                      loading={loadingLibraries}
                      disabled={Boolean(symbol)}
                      sx={{ mt: '1rem' }}
                      onChange={(_event, option) => field.onChange(option)}
                      onBlur={field.onBlur}
                      value={field.value}
                      getOptionLabel={({ name }) => name}
                      options={librariesResponse?.pages.flatMap(({ result }) => result ?? []) ?? []}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Library"
                          error={Boolean(errors.library)}
                          helperText={errors.library?.message}
                        />
                      )}
                    />
                  )}
                />
              </Box>
              <Box display="flex" flexGrow={1} style={{ marginLeft: '20px' }} gap={2}>
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ border: '1px solid #ccc', borderRadius: '4px', padding: '4px' }}
                >
                  <Box display="flex" flexDirection="column" gap={2} style={{ margin: '4px' }}>
                    <Typography variant="button">colors</Typography>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={isSubmitting}
                            checked={disableColor}
                            onChange={(e) => handleDisableColor(e.target.checked)}
                          />
                        }
                        label="Disable"
                      />
                    </Box>
                  </Box>
                  <Box display="flex" flexDirection="row">
                    <Box display="flex" flexDirection="column" style={{ margin: '4px' }}>
                      <Typography variant="button">active color</Typography>
                    </Box>
                    <Box display="flex" flexDirection="row" justifyContent="center">
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          border: '1px solid #333',
                          backgroundColor: symbolColors[selectedColor],
                          margin: '4px',
                        }}
                      />
                    </Box>
                  </Box>
                  {renderColorChoices((colorIndex) => setSelectedColor(colorIndex))}
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  sx={{ border: '1px solid #ccc', borderRadius: '4px', padding: '4px' }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={isSubmitting}
                        checked={snapToGrid}
                        onChange={(e) => setSnapToGrid(e.target.checked)}
                      />
                    }
                    label="Snap to Grid"
                  />
                  <TextField
                    variant="outlined"
                    label="Grid Size"
                    type="number"
                    disabled={isSubmitting || !snapToGrid}
                    value={snapGridSize}
                    onChange={(e) => setSnapGridSize(Number.parseInt(e.target.value, 10))}
                    style={{ width: '100px' }}
                    slotProps={{ input: { endAdornment: <InputAdornment position="end">px</InputAdornment> } }}
                  />
                </Box>
              </Box>
              <Box display="flex" flexDirection="row" sx={{ ml: 2, mb: 'auto' }} gap={2}>
                <Button
                  variant="outlined"
                  disabled={isSubmitting}
                  startIcon={<BackIcon />}
                  onClick={() => navigate('/symbols')}
                >
                  Back
                </Button>
                <LoadingButton
                  variant="contained"
                  type="submit"
                  disabled={!isValid}
                  loading={isSubmitting}
                  startIcon={<SaveIcon />}
                >
                  Save
                </LoadingButton>
              </Box>
            </Box>
            {createSymbolError || updateSymbolError ? (
              <Alert severity="error">{(createSymbolError || updateSymbolError)?.response?.data.message}</Alert>
            ) : null}
            <Box display="flex" flexDirection="row" gap={5}>
              <Box display="flex" flexDirection="column" alignItems="center" flex={1}>
                <Box
                  display="flex"
                  flexDirection="column"
                  style={{
                    width: '100%',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '4px',
                    height: '100%',
                  }}
                >
                  <Box display="flex" flexDirection="column" style={{ margin: '4px' }}>
                    <Typography variant="button">actions</Typography>
                  </Box>
                  {renderActions()}
                </Box>
              </Box>
              <Box flex={1} sx={{ border: '1px solid #e5e5e5' }}>
                <Box display="flex" sx={{ justifyContent: 'center' }}>
                  <Box id="svg" onMouseMove={handleMouseMove} onMouseUp={() => setSelectedHelper(null)}>
                    <SymbolViewer
                      width={600}
                      height={600}
                      coordinates={coordinates}
                      onComponentClick={onComponentClick}
                      onHelperClick={handleHelperMove}
                      opacity={0.4}
                    />
                  </Box>
                </Box>
                <Typography variant="button">
                  Mouse Position [ X: {mousePos.x}, Y: {mousePos.y} ]
                </Typography>
              </Box>
              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '4px', overflowY: 'auto' }}
              >
                <Box display="flex" flexDirection="row" sx={{ m: '4px' }}>
                  <Box flexShrink={0}>
                    <Typography variant="button">Selected Component</Typography>
                  </Box>
                  <Box display="flex" flexDirection="row" style={{ width: '100%' }} justifyContent="flex-end">
                    <Button variant="outlined" color="info" size="small" onClick={() => onClear()}>
                      Clear
                    </Button>
                  </Box>
                </Box>
                {renderSelectedCoordinate()}
              </Box>
            </Box>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};
